/*
 * Copyright (C) 2025 - present by Potentially
 *
 * Please see distribution for license.
 */

import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, OnDestroy, Output, computed, input } from '@angular/core';
import { linkedSignal } from '@app/app/signal-backports/linked_signal';
import {
  EditorContent,
  ExternalContentSummary,
  GeneratedReport,
  GeneratedReportConditionalSection,
  GeneratedReportSection,
  ImageUploadContent,
  ResourceSection,
  SingleUrlContent,
} from '../../../../../../shared/models';
import { Form } from '../../../../../../shared/models/editor/form-content.model';
import { MediaUploadData } from '../../../../../../shared/models/editor/media-content.model';
import { Quiz } from '../../../../../../shared/models/editor/quiz-content.model';
import {
  LearnerPlaylistReport,
  LearnerPlaylistReportFileDetails,
  LearnerPlaylistReportForm,
  LearnerPlaylistReportItem,
  LearnerPlaylistReportUploadForm,
  ReportFormNoteDetail,
} from '../../../../../../shared/models/playlist/playlist-report.model';
import { QuizReportResponse } from '../../../../../resource/models';

export interface FlatReportItem {
  title: string;
  playlistName?: string;
  thumbnail?: string;
  content?: ResourceSection[];
  forms?: LearnerPlaylistReportForm[];
  report?: GeneratedReport;
  completedOn?: string;
  submittedOn?: string;
  segments?: ContentSegment[];
  assessmentSections?: GeneratedReportConditionalSection[];
  fileDetails?: LearnerPlaylistReportFileDetails[];
}

export interface ContentSegment {
  type: 'content' | 'document' | 'form' | 'permalink' | 'external-link' | 'weblink';
  content?: EditorContent[];
  documentUrl?: string;
  documentPermalink?: string;
  documentPermalinkLabel?: string;
  formActivity?: PrintFormActivity;
  permalink?: string;
  permalinkLabel?: string;
  externalUrl?: string;
  externalLabel?: string;
  externalMediaType?: 'image' | 'pdf' | 'other';
  weblinkUrl?: string;
  weblinkTitle?: string;
  weblinkDescription?: string;
}

export interface PrintReportSection {
  title: string;
  isMainSection: boolean;
  segments: ContentSegment[];
  cards: FlatReportItem[];
}

export type FileUploadFormFileDetails = {
  name: string;
  permalink?: string;
  pdfUrl?: string;
  imageUrl?: string;
};

type PrintFormActivityBase = {
  name: string;
  empty: boolean;
  review?: 'APPROVED' | 'RESUBMIT' | 'EMPTY';
  reviewDate?: string;
  comments?: { note: ReportFormNoteDetail; index?: number }[];
};

type PrintFormActivityTextForm = PrintFormActivityBase & {
  type: 'TEXT_FORM';
  value?: string;
};

type PrintFormActivityCheckboxForm = PrintFormActivityBase & {
  type: 'CHECKBOX_FORM';
  value?: boolean;
};

type PrintFormActivityFileUploadForm = PrintFormActivityBase & {
  type: 'FILE_UPLOAD_FORM';
  value?: FileUploadFormFileDetails[];
};

type PrintFormActivityQuizForm = PrintFormActivityBase & {
  type: 'QUIZ_FORM';
  value?: { responses: QuizReportResponse[]; quiz: Quiz };
};

type PrintFormActivityCollectorForm = PrintFormActivityBase & {
  type: 'COLLECTOR_FORM';
  value?: {
    moments: { imageUrl: string; title: string }[];
    answers: string[];
  }[];
};

type PrintFormActivityUnsupportedForm = PrintFormActivityBase & {
  type: 'UNSUPPORTED_FORM';
};

export type PrintFormActivity =
  | PrintFormActivityTextForm
  | PrintFormActivityCheckboxForm
  | PrintFormActivityFileUploadForm
  | PrintFormActivityQuizForm
  | PrintFormActivityCollectorForm
  | PrintFormActivityUnsupportedForm;

@Component({
  selector: 'ptl-playlist-print-report',
  templateUrl: './print-report.component.html',
  styleUrls: ['./print-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlaylistPrintReportComponent implements OnDestroy {
  report = input<LearnerPlaylistReport>();
  @Output() printComplete = new EventEmitter<void>();
  @Output() printError = new EventEmitter<void>();

  currentTime: Date;
  // Browsers known to support working anchor links in Print-to-PDF output
  supportsPdfAnchors = /Chrom(e|ium)\//i.test(navigator.userAgent);
  playlistTitle = computed(() => this.report()?.reportItems[0]?.title);
  playlistThumbnail = computed(() => this.report()?.reportItems[0]?.thumbnail);
  reportSections = computed(() => {
    const reportItems = this.report()?.reportItems;
    if (!reportItems?.length) return [];
    const playlistItem = reportItems[0];
    return this.buildReportSections(playlistItem);
  });
  allCards = computed(() => {
    const sections = this.reportSections();
    const cards: FlatReportItem[] = [];
    for (const section of sections) {
      cards.push(...section.cards);
    }
    return cards;
  });
  pendingPdfCount = linkedSignal(() => {
    const sections = this.reportSections();
    let count = 0;
    const countSegmentPdfs = (segments: ContentSegment[]) => {
      for (const seg of segments) {
        if (seg.type === 'document' && seg.documentUrl) count++;
        if (seg.type === 'external-link' && seg.externalMediaType === 'pdf') count++;
        if (seg.type === 'form' && seg.formActivity?.type === 'FILE_UPLOAD_FORM') {
          const files = seg.formActivity.value as FileUploadFormFileDetails[] | undefined;
          count += files?.filter((f) => f.pdfUrl).length || 0;
        }
      }
    };
    for (const section of sections) {
      countSegmentPdfs(section.segments);
      for (const card of section.cards) {
        countSegmentPdfs(card.segments || []);
      }
    }
    return count;
  });
  private afterPrintHandler: () => void;
  private originalParent: HTMLElement;
  private originalNextSibling: Node | null;
  private pdfLoadResolve: (() => void) | undefined;

  constructor(private el: ElementRef<HTMLElement>) {}

  ngOnDestroy(): void {
    document.body.classList.remove('playlist-print-active');
    this.restoreElement();
    if (this.afterPrintHandler) {
      window.removeEventListener('afterprint', this.afterPrintHandler);
    }
  }

  triggerPrint(): void {
    this.currentTime = new Date();

    // Move element to document.body so it's outside ptl-index-main,
    // which gets hidden by the print CSS
    const hostEl = this.el.nativeElement;
    this.originalParent = hostEl.parentElement;
    this.originalNextSibling = hostEl.nextSibling;
    document.body.appendChild(hostEl);

    document.body.classList.add('playlist-print-active');

    // Wait for PDF renderers to finish, then wait for all images to load
    Promise.race([this.waitForPdfRenderers(), new Promise<void>((resolve) => setTimeout(resolve, 30000))])
      .then(() => this.waitForImages())
      .then(() => {
        window.print();
      });

    this.afterPrintHandler = () => {
      document.body.classList.remove('playlist-print-active');
      this.restoreElement();
      this.printComplete.emit();
      window.removeEventListener('afterprint', this.afterPrintHandler);
      this.afterPrintHandler = undefined;
    };
    window.addEventListener('afterprint', this.afterPrintHandler);
  }

  onPdfRendererLoaded(): void {
    this.pendingPdfCount.update((c) => c - 1);
    if (this.pendingPdfCount() <= 0 && this.pdfLoadResolve) {
      this.pdfLoadResolve();
      this.pdfLoadResolve = undefined;
    }
  }

  private waitForPdfRenderers(): Promise<void> {
    if (this.pendingPdfCount() <= 0) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      this.pdfLoadResolve = resolve;
    });
  }

  hasAnyContent(item: FlatReportItem): boolean {
    return !!(item.segments?.length || item.report?.sections?.length);
  }

  getGlobalCardIndex(item: FlatReportItem): number {
    return this.allCards().indexOf(item);
  }

  private restoreElement(): void {
    if (this.originalParent) {
      if (this.originalNextSibling) {
        this.originalParent.insertBefore(this.el.nativeElement, this.originalNextSibling);
      } else {
        this.originalParent.appendChild(this.el.nativeElement);
      }
      this.originalParent = undefined;
      this.originalNextSibling = undefined;
    }
  }

  private buildReportSections(playlistItem: LearnerPlaylistReportItem): PrintReportSection[] {
    if (playlistItem.type !== 'PLAYLIST') return [];
    const allCards = playlistItem.cards || [];
    const cardsById = new Map<string, LearnerPlaylistReportItem>();
    for (const card of allCards) {
      cardsById.set(card._id, card);
    }

    return playlistItem.sections.map((section, index) => {
      const sectionContent: ResourceSection[] = section.content?.length ? [{ uid: section.uid, dynamicContent: section.content }] : [];
      const sectionFlatItem: FlatReportItem = {
        title: section.title,
        content: sectionContent,
        fileDetails: playlistItem.fileDetails,
      };
      const segments = this.buildSegments(sectionFlatItem);

      const matchedCards: LearnerPlaylistReportItem[] = [];
      for (const cardId of section.cards) {
        const card = cardsById.get(cardId);
        if (card) {
          matchedCards.push(card);
        }
      }
      const cards = this.flattenItems(matchedCards, playlistItem.title);

      return {
        title: section.title,
        isMainSection: index === 0,
        segments,
        cards,
      };
    });
  }

  private flattenItems(items: LearnerPlaylistReportItem[], parentPlaylist?: string): FlatReportItem[] {
    const result: FlatReportItem[] = [];

    for (const item of items) {
      if (item.type === 'PLAYLIST' && item.cards) {
        result.push(...this.flattenItems(item.cards, item.title));
      } else if (item.type === 'WEBLINK') {
        const innerTitle = item.content?.[0]?.title || item.title;
        const description = item.content?.[0]?.description;
        const flatItem: FlatReportItem = {
          title: item.title,
          playlistName: parentPlaylist,
          thumbnail: item.thumbnail,
          forms: item.forms,
          report: item.report,
          completedOn: item.completedOn,
          submittedOn: item.submittedOn,
          fileDetails: item.fileDetails,
          segments: [
            {
              type: 'weblink',
              weblinkUrl: item.webLinkUrl,
              weblinkTitle: innerTitle,
              weblinkDescription: description,
            },
          ],
        };
        flatItem.assessmentSections = this.buildAssessmentSections(flatItem);
        result.push(flatItem);
      } else {
        const flatItem: FlatReportItem = {
          title: item.title,
          playlistName: parentPlaylist,
          thumbnail: item.thumbnail,
          content: item.content,
          forms: item.forms,
          report: item.report,
          completedOn: item.completedOn,
          submittedOn: item.submittedOn,
          fileDetails: item.fileDetails,
        };
        flatItem.segments = this.buildSegments(flatItem);
        flatItem.assessmentSections = this.buildAssessmentSections(flatItem);
        result.push(flatItem);
      }
    }

    return result;
  }

  private buildSegments(item: FlatReportItem): ContentSegment[] {
    const formActivitiesByUid = new Map<string, PrintFormActivity>();
    if (item.forms) {
      for (const form of item.forms) {
        if (form.uid) {
          formActivitiesByUid.set(form.uid, this.mapForm(form));
        }
      }
    }

    if (!item.content?.length) {
      // No content sections — return all forms as standalone segments
      return Array.from(formActivitiesByUid.values()).map((activity) => ({ type: 'form' as const, formActivity: activity }));
    }

    const fileDetails = item.fileDetails;
    const segments: ContentSegment[] = [];
    let pendingContent: EditorContent[] = [];

    const flushContent = () => {
      if (pendingContent.length) {
        segments.push({ type: 'content', content: pendingContent });
        pendingContent = [];
      }
    };

    for (const section of item.content) {
      for (const contentItem of section.dynamicContent || []) {
        if (contentItem.type === 'FORM') {
          flushContent();
          const formUid = (contentItem as Form).uid;
          const matchedActivity = formUid ? formActivitiesByUid.get(formUid) : undefined;
          if (matchedActivity) {
            segments.push({ type: 'form', formActivity: matchedActivity });
            formActivitiesByUid.delete(formUid);
          }
        } else if (this.isPermalinkOnlyContent(contentItem)) {
          // Non-IMAGE/DOCUMENT media uploads (video, audio, etc.) — show permalink only
          flushContent();
          const details = this.findFileDetails(this.extractFilePath(contentItem), fileDetails);
          if (details?.permalink) {
            segments.push({
              type: 'permalink',
              permalink: details.permalink,
              // documentPermalinkLabel: details.path.split('/').pop() || details.permalink,
              documentPermalinkLabel: details.permalink,
            });
          } else {
            // Fallback to resource-preview if no permalink available
            pendingContent.push(contentItem);
          }
        } else if (this.isDocumentContent(contentItem)) {
          flushContent();
          const details = this.findFileDetails(this.extractFilePath(contentItem), fileDetails);
          if (details) {
            const isPdf = this.isPdfFile(details.path);
            const canRenderPdf = isPdf || !!details.pdfPreviewUrl;
            segments.push({
              type: 'document',
              documentUrl: canRenderPdf ? (isPdf ? details.url : details.pdfPreviewUrl) : undefined,
              documentPermalink: details.permalink,
              // documentPermalinkLabel: details.path.split('/').pop() || details.permalink,
              documentPermalinkLabel: details.permalink,
            });
          } else {
            // Fallback: no fileDetails match, use presigned URL
            const url = this.getDocumentUrl(contentItem);
            if (url) {
              segments.push({ type: 'document', documentUrl: url });
            }
          }
        } else if (this.isImageContent(contentItem)) {
          // Image uploads — permalink + image grouped together
          flushContent();
          const details = this.findFileDetails(contentItem.fileName, fileDetails);
          segments.push({
            type: 'content',
            content: [contentItem],
            permalink: details?.permalink,
            permalinkLabel: details?.permalink,
          });
        } else if (contentItem.type === 'EXTERNAL_CONTENT_SUMMARY') {
          flushContent();
          const external = contentItem as ExternalContentSummary;
          const url = external.url;
          const label = external.title || url;
          segments.push({
            type: 'external-link',
            externalUrl: url,
            externalLabel: label,
            externalMediaType: this.getExternalMediaType(url),
          });
        } else {
          pendingContent.push(contentItem);
        }
      }
      flushContent();
    }

    // Append any forms that weren't matched to content positions
    for (const activity of formActivitiesByUid.values()) {
      segments.push({ type: 'form', formActivity: activity });
    }

    return segments;
  }

  private buildAssessmentSections(item: FlatReportItem): GeneratedReportConditionalSection[] {
    if (!item.report?.sections) return [];

    return item.report.sections
      .filter((section: GeneratedReportSection): section is GeneratedReportConditionalSection => section.type === 'CONDITIONAL_SECTION')
      .filter((section: GeneratedReportConditionalSection) => !!section.learningGoalWrapper?.competencyLevels);
  }

  private getDocumentUrl(content: EditorContent): string | undefined {
    const uploadData = content as MediaUploadData;
    if (uploadData?.content && 'url' in uploadData.content) {
      return (uploadData.content as { url: string }).url;
    }
    return undefined;
  }

  private isDocumentContent(content: EditorContent): boolean {
    return content.type === 'MEDIA_UPLOAD' && (content as MediaUploadData).mediaType === 'DOCUMENT';
  }

  private isImageContent(content: EditorContent): boolean {
    return content.type === 'MEDIA_UPLOAD' && (content as MediaUploadData).mediaType === 'IMAGE';
  }

  private isPermalinkOnlyContent(content: EditorContent): boolean {
    if (content.type !== 'MEDIA_UPLOAD') return false;
    const upload = content as MediaUploadData;
    if (upload.content?.type !== 'SINGLE_URL_CONTENT') return false;
    return upload.mediaType !== 'IMAGE' && upload.mediaType !== 'DOCUMENT';
  }

  private findFileDetails(
    filePath: string | undefined,
    fileDetails: LearnerPlaylistReportFileDetails[] | undefined,
  ): LearnerPlaylistReportFileDetails | undefined {
    if (!filePath || !fileDetails) return undefined;
    return fileDetails.find((d) => d.path === filePath);
  }

  private extractFilePath(content: EditorContent): string | undefined {
    if (content.type !== 'MEDIA_UPLOAD') return undefined;
    const upload = content as MediaUploadData;
    const mediaContent = upload.content;
    if (!mediaContent) return undefined;
    let url: string | undefined = undefined;
    switch (mediaContent.type) {
      case 'SINGLE_URL_CONTENT':
        url = (mediaContent as SingleUrlContent).url;
        break;
      case 'IMAGE_UPLOAD_CONTENT':
        url = (mediaContent as ImageUploadContent).original;
        break;
    }
    if (!url) return undefined;
    return decodeURI(new URL(url).pathname.substring(1));
  }

  private isPdfFile(path: string): boolean {
    return path.toLowerCase().endsWith('.pdf');
  }

  private isImageFile(path: string): boolean {
    const ext = path.toLowerCase().split('.').pop();
    return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext);
  }

  private getExternalMediaType(url: string): 'image' | 'pdf' | 'other' {
    try {
      const pathname = new URL(url).pathname.toLowerCase();
      if (this.isPdfFile(pathname)) return 'pdf';
      const ext = pathname.split('.').pop();
      if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return 'image';
    } catch {
      // invalid URL — treat as other
    }
    return 'other';
  }

  private mapForm(form: LearnerPlaylistReportForm): PrintFormActivity {
    const hasData = this.formHasData(form);
    const activityBase: PrintFormActivityBase = {
      name: form.title,
      empty: !hasData,
      review: form.requiresResubmit ? 'RESUBMIT' : form.acceptedOn ? 'APPROVED' : 'EMPTY',
      reviewDate: form.acceptedOn,
      comments: form.noteDetails?.map((item, i, arr) => ({
        note: item,
        index: arr.length > 1 ? i + 1 : undefined,
      })),
    };

    switch (form.type) {
      case 'TEXT_FORM':
        return {
          ...activityBase,
          type: 'TEXT_FORM',
          value: hasData ? form.value : null,
        };
      case 'CHECKBOX_FORM':
        return {
          ...activityBase,
          type: 'CHECKBOX_FORM',
          value: hasData ? form.checked : null,
        };
      case 'FILE_UPLOAD_FORM': {
        const uploadForm = form as LearnerPlaylistReportUploadForm;
        const files = uploadForm.fileDetails?.map((detail) => ({
          name: detail.path.split('/').pop() || detail.path,
          permalink: detail.permalink,
          pdfUrl: this.isPdfFile(detail.path) ? detail.url : detail.pdfPreviewUrl || undefined,
          imageUrl: this.isImageFile(detail.path) ? detail.url : undefined,
        }));
        return {
          ...activityBase,
          type: 'FILE_UPLOAD_FORM',
          value: files?.length ? files : null,
        };
      }
      case 'QUIZ_FORM': {
        return {
          ...activityBase,
          type: 'QUIZ_FORM',
          value: hasData
            ? {
                responses: form.report?.second?.responses || [],
                quiz: form.report?.first,
              }
            : null,
        };
      }
      case 'COLLECTOR_FORM': {
        return {
          ...activityBase,
          type: 'COLLECTOR_FORM',
          value: hasData
            ? form.summary?.loggedItems?.map((item) => ({
                moments: item.moments || [],
                answers: item.answers?.filter((a) => !!a.answer).map((a) => a.answer) || [],
              })) || []
            : null,
        };
      }
      default:
        return { ...activityBase, type: 'UNSUPPORTED_FORM' };
    }
  }

  private formHasData(form: LearnerPlaylistReportForm): boolean {
    switch (form?.type) {
      case 'TEXT_FORM':
        return !!form.value;
      case 'CHECKBOX_FORM': {
        return form.checked === true || form.checked === false;
      }
      case 'FILE_UPLOAD_FORM':
        return !!(form as LearnerPlaylistReportUploadForm).fileDetails?.length;
      case 'QUIZ_FORM': {
        return !!form.report?.first && !!form.report?.second?.responses?.length;
      }
      case 'COLLECTOR_FORM':
        return !!form.summary?.loggedItems?.find((item) => item?.answers?.length || item?.moments?.length);
      default:
        return false;
    }
  }

  private waitForImages(): Promise<void> {
    return new Promise((resolve) => {
      // Give Angular a tick to render, then wait for images
      setTimeout(() => {
        const images = document.querySelectorAll('.playlist-print-report img');
        if (images.length === 0) {
          resolve();
          return;
        }

        let loadedCount = 0;
        const totalImages = images.length;

        const checkDone = () => {
          loadedCount++;
          if (loadedCount >= totalImages) {
            resolve();
          }
        };

        images.forEach((element) => {
          const img = element as HTMLImageElement;
          if (img.complete) {
            checkDone();
          } else {
            img.addEventListener('load', checkDone, { once: true });
            img.addEventListener('error', checkDone, { once: true });
          }
        });

        // Safety timeout — don't wait forever
        setTimeout(() => resolve(), 10000);
      }, 500);
    });
  }

  castContentSegment(data: unknown): ContentSegment {
    return data as ContentSegment;
  }
}
